import { createFileRoute, redirect, useRouter } from '@tanstack/react-router';
import { createRouteConfig } from '../utils/routeGuard';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { trpc } from '../trpc';

// Check if site is in coming soon mode
const isComingSoonMode = () => import.meta.env.VITE_COMING_SOON_MODE === 'true';

export const Route = createFileRoute('/coming-soon')({
  beforeLoad: async () => {
    // If NOT in coming soon mode, redirect to appropriate page
    if (!isComingSoonMode()) {
      // Check if user is signed in and redirect accordingly
      const clerk = window.Clerk;
      const isSignedIn = clerk?.user !== undefined;

      throw redirect({
        to: isSignedIn ? '/dashboard' : '/',
        search: {},
        hash: '',
      });
    }
  },
  component: ComingSoon,
  ...createRouteConfig({
    isPublic: true,
    seo: {
      title: 'Trackplicant - Coming Soon',
      description:
        'Track your job applications with ease. Our AI-powered platform is launching soon.',
      ogTitle: 'Trackplicant - Coming Soon',
      ogDescription:
        'Job application tracking reimagined. Sign up to be notified when we launch.',
    },
  }),
});

function ComingSoon() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Redirect if coming soon mode is toggled off while on this page
  useEffect(() => {
    if (!isComingSoonMode()) {
      const clerk = window.Clerk;
      const isSignedIn = clerk?.user !== undefined;
      router.navigate({ to: isSignedIn ? '/dashboard' : '/' });
    }
  }, [router]);

  // tRPC mutation for email signup
  const emailSignupMutation = trpc.emailSignup.submit.useMutation({
    onMutate: () => {
      setIsProcessing(true);
    },
    onSuccess: (data: any) => {
      setIsProcessing(false);
      setSubmitted(true);
      setMessage(data.message);

      if (data.alreadyExists) {
        toast.info(data.message);
      } else {
        toast.success(data.message);
      }
    },
    onError: (error: Error) => {
      setIsProcessing(false);
      toast.error(
        error.message || 'Failed to register your email. Please try again.'
      );
      console.error('Error submitting email:', error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    emailSignupMutation.mutate({ email, userType });
  };

  return (
    <div className='min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12 bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800'>
        <div className='w-full max-w-4xl mx-auto text-center'>
        <h1 className='text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight mb-6'>
          <span className='text-blue-600'>Track</span>plicant
        </h1>

        <h2 className='text-3xl sm:text-4xl font-bold mb-8'>Coming Soon</h2>

        <p className='text-xl text-gray-600 dark:text-gray-300 mb-12 max-w-2xl mx-auto'>
          Built for job seekers who are tired of spreadsheets, missed
          follow-ups, and application chaos. Our AI-powered job search assistant
          helps you stay organized, follow up at the right time, and make
          smarter decisions—without sharing your data with job boards or
          recruiters.
        </p>

        {/* Email signup form */}
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 mb-8'>
          <h3 className='text-xl font-semibold mb-4'>
            Be the first to know when we launch
          </h3>

          {submitted ? (
            <div className='p-4 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-md'>
              <p className='font-medium text-lg'>{message}</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className='space-y-4 w-full max-w-lg mx-auto'
            >
              <div>
                <input
                  type='email'
                  placeholder='Enter your email'
                  className='w-full px-4 py-3 rounded-md border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500'
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={emailSignupMutation.isPending}
                />
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  What best describes you?
                </label>
                <div className='space-y-2'>
                  <div className='flex items-center'>
                    <input
                      type='radio'
                      id='new-grad'
                      name='user-type'
                      value='new-grad'
                      className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300'
                      checked={userType === 'new-grad'}
                      onChange={() => setUserType('new-grad')}
                      disabled={emailSignupMutation.isPending}
                    />
                    <label
                      htmlFor='new-grad'
                      className='ml-2 block text-sm text-gray-700 dark:text-gray-300'
                    >
                      New grad or student
                    </label>
                  </div>
                  <div className='flex items-center'>
                    <input
                      type='radio'
                      id='mid-career'
                      name='user-type'
                      value='mid-career'
                      className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300'
                      checked={userType === 'mid-career'}
                      onChange={() => setUserType('mid-career')}
                      disabled={emailSignupMutation.isPending}
                    />
                    <label
                      htmlFor='mid-career'
                      className='ml-2 block text-sm text-gray-700 dark:text-gray-300'
                    >
                      Mid-career job seeker
                    </label>
                  </div>
                  <div className='flex items-center'>
                    <input
                      type='radio'
                      id='freelancer'
                      name='user-type'
                      value='freelancer'
                      className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300'
                      checked={userType === 'freelancer'}
                      onChange={() => setUserType('freelancer')}
                      disabled={emailSignupMutation.isPending}
                    />
                    <label
                      htmlFor='freelancer'
                      className='ml-2 block text-sm text-gray-700 dark:text-gray-300'
                    >
                      Freelancer or contractor
                    </label>
                  </div>
                  <div className='flex items-center'>
                    <input
                      type='radio'
                      id='recruiter'
                      name='user-type'
                      value='recruiter'
                      className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300'
                      checked={userType === 'recruiter'}
                      onChange={() => setUserType('recruiter')}
                      disabled={emailSignupMutation.isPending}
                    />
                    <label
                      htmlFor='recruiter'
                      className='ml-2 block text-sm text-gray-700 dark:text-gray-300'
                    >
                      Recruiter
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <button
                  type='submit'
                  className='w-full px-6 py-3 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all disabled:opacity-70 disabled:cursor-not-allowed'
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    'Get Early Invite'
                  )}
                </button>
              </div>

              <p className='text-sm text-gray-500 dark:text-gray-400'>
                We'll only use your email to send early access updates. <br />
                🔒 We never share your data.
              </p>
            </form>
          )}
        </div>

        <div className='text-xl text-gray-600 dark:text-gray-300 mb-4 max-w-2xl mx-auto'>
          AI-powered job search support that works with your inbox—not against
          it.
        </div>
        {/* Features preview */}
        {/* <div className='grid grid-cols-1 md:grid-cols-3 gap-8 mb-12'>
          <div className='flex flex-col items-center text-center p-6 rounded-lg bg-white dark:bg-gray-800 shadow-sm'>
            <div className='w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-4'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-8 w-8 text-blue-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                />
              </svg>
            </div>
            <h3 className='text-xl font-semibold mb-2'>Import Jobs</h3>
            <p className='text-gray-600 dark:text-gray-300'>
              Automatically extract job details from URLs with our AI-powered
              importer.
            </p>
          </div>

          <div className='flex flex-col items-center text-center p-6 rounded-lg bg-white dark:bg-gray-800 shadow-sm'>
            <div className='w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-4'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-8 w-8 text-blue-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                />
              </svg>
            </div>
            <h3 className='text-xl font-semibold mb-2'>Track Progress</h3>
            <p className='text-gray-600 dark:text-gray-300'>
              Never lose track of applications, interviews, and follow-ups.
            </p>
          </div>

          <div className='flex flex-col items-center text-center p-6 rounded-lg bg-white dark:bg-gray-800 shadow-sm'>
            <div className='w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mb-4'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-8 w-8 text-blue-600'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
                />
              </svg>
            </div>
            <h3 className='text-xl font-semibold mb-2'>Get Insights</h3>
            <p className='text-gray-600 dark:text-gray-300'>
              Improve your applications with AI-powered insights and
              recommendations.
            </p>
          </div>
        </div> */}

        <div className='text-gray-500 dark:text-gray-400'>
          <p className='flex justify-center items-center mb-2'>
            <a
              href='https://x.com/Trackplicant'
              target='_blank'
              rel='me noopener noreferrer'
              className='text-gray-400 hover:text-black transition'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 1200 1227'
                className='w-5 h-5 inline mr-1'
                fill='currentColor'
              >
                <path d='M711 564l448-564h-105L651 483 316 0H0l477 683L26 1227h104l423-532 349 532h316L711 564z' />
              </svg>
              @Trackplicant
            </a>
          </p>
          <p>© {new Date().getFullYear()} Trackplicant. All rights reserved.</p>
          <p className='text-xs text-gray-400 mt-1 opacity-60'>v{import.meta.env.VITE_VERSION || '0.0.0'}</p>
        </div>
      </div>
    </div>
  );
}
